import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Chat from './components/Chat';
import AdminPanel from './components/AdminPanel';
import IdleTimeout from './components/IdleTimeout';
import { authAPI } from './services/api';
import {
  endSession,
  hasCredentialCookie,
  hasSession,
  markSession,
  notifySignedIn,
  subscribeSessionEvents,
} from './services/session';

function App() {
  // hasSession() is only a per-tab hint that avoids a login-screen flash on
  // reload; the /me call below is what actually decides.
  const [authState, setAuthState] = useState(() => (hasSession() ? 'authenticated' : 'unknown'));

  useEffect(() => {
    let mounted = true;
    const hadHint = hasSession();
    // Nothing to ask about when the browser is holding no credential at all —
    // a first visit, the login screen after signing out, a cookie that aged
    // out. /me could only answer 401 there, and it did: every load of the
    // sign-in page left a red "GET /api/auth/me 401" in the console of a page
    // that was behaving exactly as designed. hasCredentialCookie() reads the
    // non-httpOnly half of the cookie pair, which the backend issues and
    // clears in lockstep with the httpOnly session cookie, so its absence is
    // conclusive. Its presence is not: a cookie whose JWT no longer verifies
    // still has to be settled by the server, which is what the /me below is
    // for — and the per-tab hint keeps its own veto, so a session this tab
    // already knows about is always verified even if the cookie read fails.
    if (!hadHint && !hasCredentialCookie()) {
      setAuthState('unauthenticated');
      return undefined;
    }
    (async () => {
      try {
        await authAPI.getCurrentUser();
        // Record the hint here rather than only in authAPI.login/loginWithGoogle,
        // so the TELUS PingID flow — which lands on /chat via a server-side 302
        // and never touches JS — is covered too. A hint that was absent before
        // this check means the session is new to this tab (a fresh SSO landing),
        // which is exactly when a sibling tab parked on /login should follow.
        if (hadHint) markSession(true);
        else notifySignedIn();
        if (mounted) setAuthState('authenticated');
      } catch {
        if (mounted) setAuthState('unauthenticated');
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Keep every PRISM tab in the same state. Signing out in one window must not
  // leave a second one showing a usable-looking app, and signing in must not
  // leave another parked on a login form for a session that now exists.
  useEffect(() => subscribeSessionEvents((type) => {
    const onLoginPage = window.location.pathname.endsWith('/login');
    if (type === 'logout' && !onLoginPage) {
      endSession({ broadcast: false });
    } else if (type === 'login' && onLoginPage) {
      window.location.replace('/chat');
    }
  }), []);

  const PrivateRoute = ({ children }) => {
    if (authState === 'unknown') return null;
    return authState === 'authenticated' ? children : <Navigate to="/login" replace />;
  };

  // Wait for the /me answer before choosing a destination. Guessing 'login'
  // while the check is in flight would strand a user who has a perfectly valid
  // cookie but no per-tab hint — a freshly opened tab, or a bookmark — on the
  // sign-in screen.
  const homeRedirect = authState === 'unknown'
    ? null
    : <Navigate to={authState === 'authenticated' ? '/chat' : '/login'} replace />;

  return (
    <Router>
      {authState === 'authenticated' && <IdleTimeout />}
      <Routes>
        <Route
          path="/login"
          element={<Login onAuthenticated={() => setAuthState('authenticated')} />}
        />
        <Route
          path="/chat"
          element={
            <PrivateRoute>
              <Chat />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AdminPanel />
            </PrivateRoute>
          }
        />
        <Route path="/" element={homeRedirect} />
        {/* Anything else (a stale bookmark, a hand-edited URL) resolves to one
            of the two real states instead of rendering a blank page. */}
        <Route path="*" element={homeRedirect} />
      </Routes>
    </Router>
  );
}

export default App;
